/**
 * Ship detail (M6 plan P29, spec §8): the ship's status, loadout, captain and captain log beside the standing-order
 * editor. Display only; the editor's logic is in `orderModel.ts`, the reads in `shipDetailHost.tsx`. UI events only.
 */
import type { ReactNode } from 'react'
import type { FleetLogEntry, FleetShipView } from '../../strategic/types'
import { formatCredits } from '../format'
import { wareName } from '../strategic/names'
import { creditsLabel, earningsLabel, type FleetScreenConfig } from './fleetModel'
import { OrderEditor } from './OrderEditor'
import type { EditorAction, EditorOptions, EditorState, OrderEditorData } from './orderModel'

/** One fitted module: the slot's player label and the module's name (`loadoutLines`). */
export interface LoadoutLine {
  readonly slot: string
  readonly module: string
}

export interface ShipDetailProps {
  ship: FleetShipView | null
  /** The fit from the Career's hangar (null when unknown, e.g. a ship not synced yet). */
  loadout: readonly LoadoutLine[] | null
  log: readonly FleetLogEntry[]
  location: string
  config: FleetScreenConfig
  data: OrderEditorData
  opts: EditorOptions | null
  editor: EditorState | null
  dispatch: (a: EditorAction) => void
}

export function ShipDetail(p: ShipDetailProps): ReactNode {
  const s = p.ship
  if (s === null) {
    return (
      <div className="sui fl-screen sd-screen" data-testid="ship-detail">
        <p className="fl-notice">Reading the ship…</p>
      </div>
    )
  }
  const c = s.captain
  return (
    <div className="sui fl-screen sd-screen" data-testid="ship-detail" data-ship={s.id}>
      <header className="sui-head">
        <h2 className="sui-title">{s.name}</h2>
        <span className="fl-count">
          {p.config.classes[s.shipClass]} · {p.config.statuses[s.status]} · {p.location} · hull {Math.round(s.hp * 100)}%{s.insured ? ' · insured' : ''}
        </span>
      </header>
      <div className="sd-cols">
        <div className="sd-info">
          <section aria-label="Loadout" data-testid="ship-loadout">
            <h3 className="sd-h">Loadout</h3>
            {p.loadout === null || p.loadout.length === 0 ? (
              <p className="oe-dim">No fit on record</p>
            ) : (
              <dl className="sd-dl">
                {p.loadout.map((l) => (
                  <div key={l.slot}>
                    <dt>{l.slot}</dt>
                    <dd>{l.module}</dd>
                  </div>
                ))}
              </dl>
            )}
            <p className="oe-dim">Cargo: {s.cargo === null ? 'empty' : `${Math.round(s.cargo.qty)} t ${wareName(s.cargo.ware)}`}</p>
            <p className="oe-dim" data-testid="ship-earnings">
              Earned today {creditsLabel(s.earnedToday)} · yesterday {creditsLabel(s.earnedYesterday)} · average {earningsLabel(s.earnedPerDay)} · {creditsLabel(s.earnedTotal)} total
            </p>
          </section>
          <section aria-label="Captain" data-testid="ship-captain">
            <h3 className="sd-h">Captain</h3>
            {c === null ? (
              <p className="oe-dim">No captain. Hire one at a station's captain roster.</p>
            ) : (
              <>
                <p>
                  {c.name} · wage {formatCredits(c.wage)} cr/day · loyalty {Math.round(c.loyalty)}
                </p>
                <p className="oe-dim">
                  {Object.entries(c.skills)
                    .map(([k, v]) => `${k} ${v}`)
                    .join(' · ')}
                  {c.traits.length > 0 ? ` · ${c.traits.join(', ')}` : ''}
                </p>
              </>
            )}
          </section>
          <section aria-label="Captain log" data-testid="ship-log">
            <h3 className="sd-h">Log</h3>
            {p.log.length === 0 ? (
              <p className="oe-dim">Nothing logged yet</p>
            ) : (
              <ul className="sd-log">
                {p.log.map((e) => (
                  <li key={e.id}>
                    <span className="oe-dim">Day {e.day}</span> {e.text}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
        {p.editor !== null && p.opts !== null ? <OrderEditor state={p.editor} data={p.data} opts={p.opts} orderLabels={p.config.orders} dispatch={p.dispatch} /> : null}
      </div>
      <footer className="fl-help">↑↓ row · ←→ change · Enter or Space toggle / press · Esc back to the fleet</footer>
    </div>
  )
}
